import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    Grid,
    TextField,
    MenuItem,
    RadioGroup,
    Radio,
    FormControlLabel,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    Stack,
    Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { axiosInstance } from '../../../api';

const STEPS = [
    'Manufacturer',
    'Device Model',
    'Connection Method',
    'Network Config',
    'Connector Binding',
    'Test Connection',
    'User Mapping',
    'Sync Schedule',
    'Initial Sync',
    'Summary',
];

const AddDeviceWizard = ({ onComplete, onCancel }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    // Catalogs
    const [manufacturers, setManufacturers] = useState([]);
    const [models, setModels] = useState([]);
    const [connectors, setConnectors] = useState([]);

    // Form Data
    const [formData, setFormData] = useState({
        manufacturerSlug: 'simulator',
        modelId: '',
        modelCode: '',
        modelName: '',
        verificationStatus: '',
        connectionMethod: 'SIMULATOR',
        name: '',
        deviceIdentifier: '',
        branchId: '',
        location: '',
        ipAddress: '127.0.0.1',
        port: 4370,
        commKey: '',
        password: '',
        connectorId: '',
        syncInterval: 15,
        syncEnabled: true,
    });

    // Step 6: Test Result
    const [testResult, setTestResult] = useState(null);
    // Step 9: Sync Result
    const [createdDeviceId, setCreatedDeviceId] = useState(null);
    const [initialSyncResult, setInitialSyncResult] = useState(null);

    useEffect(() => {
        const loadInitialData = async () => {
            setCatalogLoading(true);
            setErrorMsg(null);
            try {
                const [manRes, connRes] = await Promise.all([
                    axiosInstance.get('/api/attendance/biometric/manufacturers/'),
                    axiosInstance.get('/api/attendance/biometric/connectors/'),
                ]);
                const manList = manRes.data?.manufacturers || [];
                setManufacturers(manList);
                setConnectors(connRes.data?.connectors || []);

                // Load models for default simulator
                await loadModelsForManufacturer('simulator');
            } catch (err) {
                console.error("Error loading wizard initial data:", err);
                setErrorMsg(
                    err.response?.data?.error ||
                    err.response?.data?.detail ||
                    'Unable to load biometric hardware catalog from the server. Please verify that the backend service is running and biometric database migrations are applied.'
                );
            } finally {
                setCatalogLoading(false);
            }
        };
        loadInitialData();
    }, []);

    const loadModelsForManufacturer = async (slug) => {
        setModelsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/attendance/biometric/models/?manufacturer=${slug}`);
            const mList = res.data?.models || [];
            setModels(mList);
            if (mList.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    modelId: mList[0].id,
                    modelCode: mList[0].model_code,
                    modelName: mList[0].model_name,
                    verificationStatus: mList[0].verification_status,
                }));
            }
        } catch (err) {
            console.error("Error loading models:", err);
            setModels([]);
            setErrorMsg(
                err.response?.data?.error ||
                err.response?.data?.detail ||
                `Unable to load device models for manufacturer "${slug}".`
            );
        } finally {
            setModelsLoading(false);
        }
    };

    const handleManufacturerChange = (slug) => {
        setFormData((prev) => ({
            ...prev,
            manufacturerSlug: slug,
            connectionMethod: slug === 'simulator' ? 'SIMULATOR' : 'LOCAL_CONNECTOR',
            ipAddress: slug === 'simulator' ? '127.0.0.1' : '',
        }));
        loadModelsForManufacturer(slug);
    };

    const handleModelSelect = (m) => {
        setFormData((prev) => ({
            ...prev,
            modelId: m.id,
            modelCode: m.model_code,
            modelName: m.model_name,
            verificationStatus: m.verification_status,
        }));
    };

    const handleRunTest = async () => {
        setLoading(true);
        setTestResult(null);
        setErrorMsg(null);
        try {
            // For uncreated device, test in simulator mode or direct validation
            if (formData.connectionMethod === 'SIMULATOR') {
                setTestResult({
                    success: true,
                    status: 'ONLINE',
                    latency_ms: 14,
                    firmware_version: 'SIM-FW-v2.4.1',
                    is_simulation: true,
                });
            } else {
                setTestResult({
                    success: false,
                    status: 'NOT_IMPLEMENTED',
                    error: 'Hardware driver requires live physical device. Please use Simulator for testing.',
                    is_simulation: false,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDevice = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const payload = {
                name: formData.name || `${formData.modelCode} Terminal`,
                device_identifier: formData.deviceIdentifier || `DEV-${Date.now().toString().slice(-4)}`,
                model_id: formData.modelId,
                connection_method: formData.connectionMethod,
                ip_address: formData.ipAddress,
                port: formData.port,
                comm_key: formData.commKey,
                password: formData.password,
                connector_id: formData.connectorId || null,
                sync_enabled: formData.syncEnabled,
                sync_interval: formData.syncInterval,
            };
            const res = await axiosInstance.post('/api/attendance/biometric/devices/', payload);
            setCreatedDeviceId(res.data.device_id);
            return res.data.device_id;
        } catch (err) {
            setErrorMsg(err.response?.data?.error || 'Failed to register device.');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleInitialSync = async (devId) => {
        const idToSync = devId || createdDeviceId;
        if (!idToSync) return;
        setLoading(true);
        try {
            const res = await axiosInstance.post(`/api/attendance/biometric/devices/${idToSync}/sync/`);
            setInitialSyncResult(res.data);
        } catch (err) {
            setErrorMsg(err.response?.data?.error || 'Initial sync failed.');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async () => {
        setErrorMsg(null);

        // Validation per step
        if (activeStep === 3) {
            if (!formData.name.trim()) {
                setFormData((p) => ({ ...p, name: `${p.modelCode} Terminal` }));
            }
            if (!formData.deviceIdentifier.trim()) {
                setFormData((p) => ({ ...p, deviceIdentifier: `DEV-${Date.now().toString().slice(-4)}` }));
            }
        }

        if (activeStep === 5) {
            // Save device before step 7 mapping / step 8 sync
            const devId = await handleSaveDevice();
            if (!devId) return; // Halt on error
        }

        if (activeStep === 7) {
            // Run initial sync at step 9
            handleInitialSync(createdDeviceId);
        }

        setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: '#fff', border: '1px solid #eef0f4' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
                    Biometric Integration Setup Wizard
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Follow the guided steps to register and test your attendance terminal.
                </Typography>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel sx={{ '& .MuiStepLabel-label': { fontWeight: 600, fontSize: '0.8rem' } }}>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {errorMsg && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }} onClose={() => setErrorMsg(null)}>
                    {errorMsg}
                </Alert>
            )}

            {/* Step 0: Manufacturer Selection */}
            {activeStep === 0 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 1: Select Hardware Manufacturer
                    </Typography>
                    {catalogLoading && manufacturers.length === 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
                            <CircularProgress size={22} />
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Loading supported hardware manufacturers...
                            </Typography>
                        </Box>
                    ) : manufacturers.length === 0 ? (
                        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                            No biometric hardware manufacturers are currently available from the server. Please verify that the backend service is reachable and biometric catalog migrations have been applied.
                        </Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {manufacturers.map((m) => {
                                const isSelected = formData.manufacturerSlug === m.slug;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={m.slug}>
                                        <Card
                                            variant="outlined"
                                            onClick={() => handleManufacturerChange(m.slug)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: '12px',
                                                borderColor: isSelected ? '#2563eb' : '#e5e7eb',
                                                bgcolor: isSelected ? '#eff6ff' : '#fff',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <CardContent>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: isSelected ? '#1e40af' : '#111827' }}>
                                                    {m.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#6b7280', mt: 1, minHeight: 40 }}>
                                                    {m.description || 'Enterprise biometric solution'}
                                                </Typography>
                                                <Chip label={`${m.model_count || 2} models`} size="small" sx={{ mt: 1.5, fontWeight: 600 }} />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            )}

            {/* Step 1: Model Selection */}
            {activeStep === 1 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 2: Select Device Model & Verification Status
                    </Typography>
                    {modelsLoading && models.length === 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
                            <CircularProgress size={22} />
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Loading device models...
                            </Typography>
                        </Box>
                    ) : models.length === 0 ? (
                        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                            No verified device models are available for the selected manufacturer.
                        </Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {models.map((m) => {
                                const isSelected = formData.modelId === m.id;
                                return (
                                    <Grid item xs={12} md={6} key={m.id}>
                                        <Card
                                            variant="outlined"
                                            onClick={() => handleModelSelect(m)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: '12px',
                                                borderColor: isSelected ? '#2563eb' : '#e5e7eb',
                                                bgcolor: isSelected ? '#eff6ff' : '#fff',
                                            }}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        {m.model_name}
                                                    </Typography>
                                                    <Chip
                                                        label={m.verification_status}
                                                        size="small"
                                                        color={m.verification_status === 'SIMULATION_TESTED' ? 'info' : (m.verification_status === 'IMPLEMENTED' ? 'success' : 'default')}
                                                        sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                                                    Code: {m.model_code} | Adapter: {m.adapter_identifier}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#4b5563', mt: 1 }}>
                                                    {m.notes || 'Supports standard punch event logging.'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            )}

            {/* Step 2: Connection Method */}
            {activeStep === 2 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 3: Select Communication Method
                    </Typography>
                    <RadioGroup
                        value={formData.connectionMethod}
                        onChange={(e) => setFormData((p) => ({ ...p, connectionMethod: e.target.value }))}
                    >
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
                            <FormControlLabel
                                value="LOCAL_CONNECTOR"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Local Connector Agent (Recommended for Customer LAN)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                            The connector runs inside your office network and communicates via Outbound HTTPS. No open firewall ports required.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
                            <FormControlLabel
                                value="DIRECT_IP"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Direct IP / VPN Connection
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                            For devices with static public IP, direct LAN, or Site-to-Site VPN access.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
                            <FormControlLabel
                                value="SIMULATOR"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Virtual Simulator Mode (Testing & Verification)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                            Simulate punch generation, offline states, and clock drift without physical hardware.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    </RadioGroup>
                </Box>
            )}

            {/* Step 3: Network & Hardware Settings */}
            {activeStep === 3 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 4: Device Identity & Network Settings
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Device Name"
                                value={formData.name}
                                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                                placeholder="e.g. 1st Floor Reception"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Device Identifier / Serial Number"
                                value={formData.deviceIdentifier}
                                onChange={(e) => setFormData((p) => ({ ...p, deviceIdentifier: e.target.value }))}
                                placeholder="e.g. ZK-REC-01"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Local IP Address / Host"
                                value={formData.ipAddress}
                                onChange={(e) => setFormData((p) => ({ ...p, ipAddress: e.target.value }))}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Communication Port"
                                type="number"
                                value={formData.port}
                                onChange={(e) => setFormData((p) => ({ ...p, port: parseInt(e.target.value) || 4370 }))}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Device Communication Key (Optional)"
                                type="password"
                                value={formData.commKey}
                                onChange={(e) => setFormData((p) => ({ ...p, commKey: e.target.value }))}
                                size="small"
                                helperText="Encrypted at rest with AES-256 / Fernet key"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Location / Building Description"
                                value={formData.location}
                                onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Step 4: Connector Binding */}
            {activeStep === 4 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 5: Bind Local Connector
                    </Typography>
                    {formData.connectionMethod === 'SIMULATOR' ? (
                        <Alert severity="info" sx={{ borderRadius: '10px' }}>
                            Simulation mode does not require an external local connector agent.
                        </Alert>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 2 }}>
                                Select the local connector agent deployed on the device subnet:
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Select Connector"
                                value={formData.connectorId}
                                onChange={(e) => setFormData((p) => ({ ...p, connectorId: e.target.value }))}
                            >
                                <MenuItem value="">-- Direct IP (No Connector) --</MenuItem>
                                {connectors.map((c) => (
                                    <MenuItem key={c.connector_id} value={c.connector_id}>
                                        {c.name} ({c.heartbeat_status})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}
                </Box>
            )}

            {/* Step 5: Test Connection */}
            {activeStep === 5 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 6: Test Device Connectivity
                    </Typography>
                    <Button variant="contained" onClick={handleRunTest} disabled={loading} sx={{ mb: 3, borderRadius: '8px', textTransform: 'none' }}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Run Connectivity Diagnostic'}
                    </Button>
                    {testResult && (
                        <Paper variant="outlined" sx={{ p: 2.5, maxWidth: 500, mx: 'auto', borderRadius: '12px', textAlign: 'left' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {testResult.success ? <CheckCircleIcon color="success" /> : <WarningAmberIcon color="warning" />}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Status: {testResult.status}
                                </Typography>
                            </Box>
                            {testResult.success ? (
                                <Typography variant="body2" sx={{ color: '#16a34a' }}>
                                    Device responded in {testResult.latency_ms}ms. Firmware: {testResult.firmware_version}
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#dc2626' }}>
                                    {testResult.error}
                                </Typography>
                            )}
                        </Paper>
                    )}
                </Box>
            )}

            {/* Step 6: User Mapping Notice */}
            {activeStep === 6 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 7: Employee Biometric ID Mapping
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
                        Device is now registered! You can map biometric user numbers (e.g. 101, 102) to CapitalBridge employees now or manage them anytime under the "Employee Mapping" tab.
                    </Alert>
                    <Typography variant="body2" sx={{ color: '#4b5563' }}>
                        Unmapped punches will remain stored in isolated staging and can be mapped retroactively at any time with automatic backfill.
                    </Typography>
                </Box>
            )}

            {/* Step 7: Sync Schedule */}
            {activeStep === 7 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 8: Configure Synchronization Schedule
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Polling / Sync Interval"
                                size="small"
                                value={formData.syncInterval}
                                onChange={(e) => setFormData((p) => ({ ...p, syncInterval: parseInt(e.target.value) }))}
                            >
                                <MenuItem value={5}>Every 5 minutes</MenuItem>
                                <MenuItem value={15}>Every 15 minutes (Standard)</MenuItem>
                                <MenuItem value={30}>Every 30 minutes</MenuItem>
                                <MenuItem value={60}>Every 60 minutes</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Step 8: Initial Sync */}
            {activeStep === 8 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Step 9: Run Initial Synchronization
                    </Typography>
                    {loading ? (
                        <CircularProgress />
                    ) : initialSyncResult ? (
                        <Paper variant="outlined" sx={{ p: 3, maxWidth: 500, mx: 'auto', borderRadius: '12px', textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#16a34a', mb: 1 }}>
                                Initial Sync Completed ({initialSyncResult.status})
                            </Typography>
                            <Typography variant="body2">Records Received: {initialSyncResult.records_received}</Typography>
                            <Typography variant="body2">Punches Staged: {initialSyncResult.records_staged}</Typography>
                            <Typography variant="body2">Duplicates Skipped: {initialSyncResult.duplicates_skipped}</Typography>
                            <Typography variant="body2">Unmapped Punches: {initialSyncResult.unmapped_count}</Typography>
                        </Paper>
                    ) : (
                        <Button variant="contained" onClick={() => handleInitialSync(createdDeviceId)} sx={{ borderRadius: '8px', textTransform: 'none' }}>
                            Trigger Initial Sync Now
                        </Button>
                    )}
                </Box>
            )}

            {/* Step 9: Summary */}
            {activeStep === 9 && (
                <Box sx={{ py: 2 }}>
                    <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>
                        Device setup completed successfully! Your biometric device is registered and configured.
                    </Alert>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Integration Summary:
                    </Typography>
                    <Typography variant="body2"><strong>Device:</strong> {formData.name} ({formData.deviceIdentifier})</Typography>
                    <Typography variant="body2"><strong>Manufacturer:</strong> {formData.manufacturerSlug.toUpperCase()}</Typography>
                    <Typography variant="body2"><strong>Connection Method:</strong> {formData.connectionMethod}</Typography>
                    <Typography variant="body2"><strong>Sync Interval:</strong> {formData.syncInterval} minutes</Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        Note: Biometric punches remain safely staged in isolation. You can review reconciliation previews anytime under Attendance.
                    </Typography>
                </Box>
            )}

            {/* Wizard Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #f3f4f6' }}>
                <Button disabled={activeStep === 0 || activeStep === 9} onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
                    Back
                </Button>
                <Stack direction="row" spacing={1.5}>
                    <Button onClick={onCancel} sx={{ textTransform: 'none' }}>
                        {activeStep === 9 ? 'Close' : 'Cancel'}
                    </Button>
                    {activeStep < 9 ? (
                        <Button variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>
                            {activeStep === 8 ? 'Finish' : 'Next'}
                        </Button>
                    ) : (
                        <Button variant="contained" color="success" onClick={onComplete} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>
                            Go to Dashboard
                        </Button>
                    )}
                </Stack>
            </Box>
        </Paper>
    );
};

export default AddDeviceWizard;

